import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSearchParam } from 'react-use';
import { v4 as uuid } from 'uuid';
import {
    selectConnectionQualityByPeerID,
    selectLocalPeer,
    useAVToggle,
    useHMSActions,
    useHMSStore,
} from '@100mslive/react-sdk';
import { Flex } from '@100mslive/roomkit-react';
import PreviewJoin from './Preview/PreviewJoin/PreviewJoin';
import { candidateCallStart, getSessionToken, verifyRoom } from '../redux/action';
import { useSetUiSettings, useTokenEndpoint } from './AppData/useUISettings';
import getToken from '../services/tokenService';
import { decryptData, isMobileOrTabletDevice } from '../utils/helper';
import {
    QUERY_PARAM_AUTH_TOKEN,
    QUERY_PARAM_PREVIEW_AS_ROLE,
    QUERY_PARAM_SKIP_PREVIEW,
    QUERY_PARAM_SKIP_PREVIEW_HEADFUL,
    UI_SETTINGS,
} from '../common/constants';
import logo from '../assets/images/incruiter_logo.svg';
import { ToastManager } from './Toast/ToastManager';
const env = 'prod';
const PreviewScreen = React.memo(() => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const hmsActions = useHMSActions();
    const tokenEndpoint = useTokenEndpoint();
    const [form, setForm] = useState(null);
    const [isMount, setIsMount] = useState(false);
    const [, setIsHeadless] = useSetUiSettings(UI_SETTINGS.isHeadless);
    const { roomId: urlRoomId, role: userRole } = useParams(); // from the url
    const [token, setToken] = useState(null);
    const [isNetworkError, setIsNetworkError] = useState(false);
    const [checkNetworkError, setCheckNetworkError] = useState(true);
    const [permissionsChecks, setPermissionsChecks] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const { isLocalAudioEnabled, isLocalVideoEnabled } = useAVToggle();
    const localPeer = useHMSStore(selectLocalPeer);
    const peerId = localPeer?.id;
    const downlinkQuality = useHMSStore(selectConnectionQualityByPeerID(peerId))?.downlinkQuality;
    // way to skip preview for automated tests, beam recording and streaming
    const beamInToken = useSearchParam('token') === 'beam_recording'; // old format to remove
    let skipPreview = useSearchParam(QUERY_PARAM_SKIP_PREVIEW) === 'true';
    // use this field to join directly for quick testing while in local
    const directJoinHeadFulFromEnv = false;
    const directJoinHeadFul = useSearchParam(QUERY_PARAM_SKIP_PREVIEW_HEADFUL) === 'true' || directJoinHeadFulFromEnv;
    skipPreview = skipPreview || beamInToken || directJoinHeadFul;
    const previewAsRole = useSearchParam(QUERY_PARAM_PREVIEW_AS_ROLE);
    let authToken = useSearchParam(QUERY_PARAM_AUTH_TOKEN);

    const [microsoftKeys, setMicrosoftKeys] = useState([]);
    useEffect(() => {
        const fetchKeysAndSet = async () => {
            const keys = 'microsoftKey';
            dispatch(
                getSessionToken({
                    keys,
                    callback: async (data) => {
                        let tmpMKeys2 = [];

                        const tmpMKeys = data
                            .filter((item) => item.key === 'microsoftKey')
                            .map((item) => {
                                const key = `${item?._id}${item?._id}`.slice(0, 32);
                                return { ...item, value: decryptData(item.value, key) };
                            });

                        tmpMKeys2 = tmpMKeys.filter((itm) => itm?.additionalValue !== 'brazilsouth');
                        setMicrosoftKeys(tmpMKeys2);
                    },
                })
            );
        };

        fetchKeysAndSet();
    }, [dispatch, location]);

    useEffect(() => {
        if (checkNetworkError && downlinkQuality) {
            if (downlinkQuality <= 3) setIsNetworkError(true);
            else setIsNetworkError(false);
        }
    }, [downlinkQuality]);

    useEffect(() => {
        if (downlinkQuality > 3 && isLocalAudioEnabled && isLocalVideoEnabled) setPermissionsChecks(true);
        else setPermissionsChecks(false);
    }, [downlinkQuality, isLocalAudioEnabled, isLocalVideoEnabled]);

    useEffect(() => {
        if (authToken) {
            setToken(authToken);
            return;
        }
        if (!tokenEndpoint || !urlRoomId) {
            return;
        }
        const roomCode = !userRole && urlRoomId;

        const getTokenFn = roomCode
            ? () => hmsActions.getAuthTokenByRoomCode({ roomCode }, { endpoint: '' })
            : () => getToken(tokenEndpoint, uuid(), userRole, urlRoomId);

        getTokenFn()
            .then((token) => {
                setToken(token);
            })
            .catch((error) => {
                console.log(error);
            });
    }, [hmsActions, tokenEndpoint, urlRoomId, userRole, authToken]);

    const onJoin = async () => {
        // Mark the function as async
        !directJoinHeadFul && setIsHeadless(skipPreview);
        const meetingURL = `/room/meeting/${userRole}/${urlRoomId}`;
        if (userRole === 'candidate') {
            dispatch(
                candidateCallStart({
                    roomId: urlRoomId,
                    callback: (status, creditExpired = false, resumeData = {}) => {
                        if (creditExpired) {
                            setIsButtonDisabled(true);
                            ToastManager.addToast({
                                title: 'Your Credits have expired',
                            });
                        } else if (status)
                            navigate(meetingURL, {
                                state: {
                                    ...form,
                                    microsoftKeys,
                                    isResume: resumeData?.isResume || false,
                                    transcriptDetails: resumeData?.transcriptDetails || form?.transcriptDetails || '',
                                    questions: resumeData?.questions,
                                    codingQuestions: resumeData?.codingQuestions,
                                    codingFeedbackQuestions: resumeData?.codingFeedbackQuestions,
                                },
                            }); //  pass env key with form
                        else if (resumeData?.resumeWindowExpired)
                            navigate(`/room/verify/${userRole}/${urlRoomId}`);
                        else setIsButtonDisabled(true);
                    },
                })
            );
        } else {
            navigate(meetingURL, { state: { ...form, microsoftKeys } }); //  pass env key with form
        }
    };

    const roomVerifyStatus = useSelector((state) => state.Interview.roomVerifyStatus);
    useEffect(() => {
        if (roomVerifyStatus) {
            setForm((prevState) => ({
                ...prevState,
                interviewId: roomVerifyStatus?._id,
                candidateName: roomVerifyStatus?.candidate?.fullName,
                positionTitle: roomVerifyStatus?.position,
                candidateEmail: roomVerifyStatus?.candidate?.email,
                candidatePhone: roomVerifyStatus?.candidate?.whatsappNumber,
                transcriptDetails: roomVerifyStatus?.transcriptDetails || '',
                // A resumable in-progress session was already verified when it
                // was first started - don't force OTP/consent again.
                ...(roomVerifyStatus?.isResumable
                    ? { otpVerified: true, instructionAccepted: true, isResume: true }
                    : {}),
            }));
        }
    }, [roomVerifyStatus]);
    useEffect(() => {
        if (location) {
            if (location?.state) setForm(location?.state);
            else dispatch(verifyRoom({ roomId: urlRoomId }));
        }
    }, [dispatch, location, urlRoomId]);

    useEffect(() => {
        if (userRole === 'candidate') {
            if (
                (roomVerifyStatus && !roomVerifyStatus?.status) ||
                (form?.interviewId &&
                    !(form?.otpVerified && form?.instructionAccepted) &&
                    !roomVerifyStatus?.isResumable)
            ) {
                const meetingURL = `/room/verify/${userRole}/${urlRoomId}`;
                navigate(meetingURL);
            }
        }
    }, [form, userRole, urlRoomId, navigate, roomVerifyStatus]);

    return (
        <Flex
            direction="column"
            css={{ size: '100%', background: 'rgb(25 25 36)', position: 'relative' }}
            className={isMount ? 'mounted' : ''}
        >
            {!isMobileOrTabletDevice() && (
                <img
                    src={logo}
                    style={{ position: 'absolute', top: 20, left: 30, height: 40, width: 'auto', zIndex: 1 }}
                    alt="Logo"
                />
            )}
            <PreviewJoin
                skipPreview={skipPreview}
                form={form}
                env={env}
                setForm={setForm}
                onJoin={onJoin}
                token={token}
                asRole={previewAsRole}
                setIsMount={setIsMount}
                isNetworkError={isNetworkError}
                permissionsChecks={permissionsChecks}
                downlinkQuality={downlinkQuality}
                setCheckNetworkError={setCheckNetworkError}
                isButtonDisabled={isButtonDisabled}
                microsoftKeys={microsoftKeys}
            />
        </Flex>
    );
});

export default PreviewScreen;
